var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Vector2Utils } from '../../../Types/IVector2';
import { PriorityQueue } from '../../../Utils/PriorityQueue';
var AStarNode = (function () {
    function AStarNode(node, gCost, hCost, parent) {
        if (gCost === void 0) { gCost = 0; }
        if (hCost === void 0) { hCost = 0; }
        if (parent === void 0) { parent = null; }
        this.priority = 0;
        this.gCost = 0;
        this.hCost = 0;
        this.parent = null;
        this.hash = 0;
        this.node = node;
        this.gCost = gCost;
        this.hCost = hCost;
        this.priority = gCost + hCost;
        this.parent = parent;
        this.hash = Vector2Utils.toHash(node);
    }
    AStarNode.prototype.updateCosts = function (gCost, hCost, parent) {
        if (parent === void 0) { parent = null; }
        this.gCost = gCost;
        this.hCost = hCost;
        this.priority = gCost + hCost;
        this.parent = parent;
    };
    AStarNode.prototype.updateNode = function (node, gCost, hCost, parent) {
        if (gCost === void 0) { gCost = 0; }
        if (hCost === void 0) { hCost = 0; }
        if (parent === void 0) { parent = null; }
        this.node = node;
        this.gCost = gCost;
        this.hCost = hCost;
        this.priority = gCost + hCost;
        this.parent = parent;
        this.hash = Vector2Utils.toHash(node);
    };
    AStarNode.prototype.reset = function () {
        this.node = null;
        this.priority = 0;
        this.gCost = 0;
        this.hCost = 0;
        this.parent = null;
        this.hash = 0;
    };
    return AStarNode;
}());
var AStarPathfinder = (function () {
    function AStarPathfinder() {
    }
    AStarPathfinder._getNode = function (node, gCost, hCost, parent) {
        if (gCost === void 0) { gCost = 0; }
        if (hCost === void 0) { hCost = 0; }
        if (parent === void 0) { parent = null; }
        var astarNode = this._nodePool.pop();
        if (!astarNode) {
            astarNode = new AStarNode(node, gCost, hCost, parent);
        }
        else {
            astarNode.updateNode(node, gCost, hCost, parent);
        }
        return astarNode;
    };
    AStarPathfinder._recycleNode = function (node) {
        if (this._nodePool.length < 1000) {
            node.reset();
            this._nodePool.push(node);
        }
    };
    AStarPathfinder.search = function (graph, start, goal) {
        var openSet = new PriorityQueue();
        var closedSet = new Set();
        var openSetMap = new Map();
        var startHash = Vector2Utils.toHash(start);
        var goalHash = Vector2Utils.toHash(goal);
        if (startHash === goalHash) {
            return { found: true, goalNode: this._getNode(start, 0, 0) };
        }
        if (!graph.isNodePassable(start)) {
            return { found: false, openSetNodes: [] };
        }
        var startNode = this._getNode(start, 0, graph.heuristic(start, goal));
        openSet.enqueue(startNode);
        openSetMap.set(startHash, startNode);
        var goalNode;
        var processedNodes = [];
        while (!openSet.isEmpty) {
            var current = openSet.dequeue();
            var currentHash = current.hash;
            openSetMap.delete(currentHash);
            if (currentHash === goalHash) {
                goalNode = current;
                break;
            }
            closedSet.add(currentHash);
            for (var _i = 0, _a = graph.getNeighbors(current.node); _i < _a.length; _i++) {
                var neighbor = _a[_i];
                var neighborHash = Vector2Utils.toHash(neighbor);
                if (closedSet.has(neighborHash)) {
                    continue;
                }
                var tentativeGScore = current.gCost + graph.cost(current.node, neighbor);
                var existingNode = openSetMap.get(neighborHash);
                if (existingNode) {
                    if (tentativeGScore < existingNode.gCost) {
                        var hCost = existingNode.hCost;
                        existingNode.updateCosts(tentativeGScore, hCost, current);
                    }
                }
                else {
                    var hCost = graph.heuristic(neighbor, goal);
                    var neighborNode = this._getNode(neighbor, tentativeGScore, hCost, current);
                    openSet.enqueue(neighborNode);
                    openSetMap.set(neighborHash, neighborNode);
                }
            }
            processedNodes.push(current);
        }
        var remainingNodes = [];
        while (!openSet.isEmpty) {
            remainingNodes.push(openSet.dequeue());
        }
        var allNodesToRecycle = __spreadArray(__spreadArray([], processedNodes, true), remainingNodes, true);
        return { found: !!goalNode, goalNode: goalNode, openSetNodes: allNodesToRecycle };
    };
    AStarPathfinder.searchPath = function (graph, start, goal) {
        var result = this.search(graph, start, goal);
        if (!result.found || !result.goalNode) {
            if (result.openSetNodes) {
                for (var _i = 0, _a = result.openSetNodes; _i < _a.length; _i++) {
                    var node = _a[_i];
                    this._recycleNode(node);
                }
            }
            return [];
        }
        var path = this.reconstructPathFromNode(result.goalNode);
        if (result.openSetNodes) {
            for (var _b = 0, _c = result.openSetNodes; _b < _c.length; _b++) {
                var node = _c[_b];
                this._recycleNode(node);
            }
        }
        return path;
    };
    AStarPathfinder.reconstructPathFromNode = function (goalNode) {
        this._tempPath.length = 0;
        var current = goalNode;
        while (current) {
            this._tempPath.unshift(current.node);
            current = current.parent;
        }
        return __spreadArray([], this._tempPath, true);
    };
    AStarPathfinder.hasPath = function (graph, start, goal) {
        var result = this.search(graph, start, goal);
        if (result.goalNode) {
            this._recycleNode(result.goalNode);
        }
        if (result.openSetNodes) {
            for (var _i = 0, _a = result.openSetNodes; _i < _a.length; _i++) {
                var node = _a[_i];
                if (node !== result.goalNode) {
                    this._recycleNode(node);
                }
            }
        }
        return result.found;
    };
    AStarPathfinder.clearPool = function () {
        this._nodePool.length = 0;
        this._tempPath.length = 0;
    };
    AStarPathfinder.getPoolStats = function () {
        return {
            poolSize: this._nodePool.length,
            maxPoolSize: 1000
        };
    };
    AStarPathfinder._nodePool = [];
    AStarPathfinder._tempPath = [];
    return AStarPathfinder;
}());
export { AStarPathfinder };
